import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CANONICAL_FEATURE_DEFINITION_BY_KEY,
  CANONICAL_FEATURE_DEFINITIONS,
  CanonicalFeatureKey,
} from "@/utils/accessControl";
import { useCreateFeature, useFeatures } from "../hooks/useFeatures";

type FeatureType = "BOOLEAN" | "NUMBER";

interface CreateFeatureDialogProps {
  open: boolean;
  onClose: () => void;
}

function parseOptionalFiniteNumber(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return undefined;
  }

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export const CreateFeatureDialog: React.FC<CreateFeatureDialogProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const { mutateAsync, isPending } = useCreateFeature();
  const { data: existingFeatures = [] } = useFeatures(open);

  const [selectedFeatureKey, setSelectedFeatureKey] = useState<
    CanonicalFeatureKey | ""
  >("");
  const [description, setDescription] = useState("");
  const [limitInput, setLimitInput] = useState("");
  const [unitPriceInput, setUnitPriceInput] = useState("");

  const existingFeatureKeys = useMemo(
    () => new Set(existingFeatures.map((feature) => feature.key)),
    [existingFeatures],
  );
  const availableFeatures = useMemo(
    () =>
      CANONICAL_FEATURE_DEFINITIONS.filter(
        (feature) => !existingFeatureKeys.has(feature.key),
      ),
    [existingFeatureKeys],
  );
  const selectedFeature =
    selectedFeatureKey === ""
      ? null
      : CANONICAL_FEATURE_DEFINITION_BY_KEY[selectedFeatureKey];
  const featureType: FeatureType = selectedFeature?.type ?? "BOOLEAN";

  const reset = () => {
    setSelectedFeatureKey("");
    setDescription("");
    setLimitInput("");
    setUnitPriceInput("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const normalizedTitle = selectedFeature?.title.trim() ?? "";
    const normalizedKey = selectedFeature?.key.trim() ?? "";
    const normalizedDescription = description.trim();
    const unitPrice = parseOptionalFiniteNumber(unitPriceInput);

    if (normalizedKey === "") {
      toast.error(t("subscriptions.features.create.validation.key"));
      return;
    }

    if (unitPriceInput.trim() !== "" && unitPrice === undefined) {
      toast.error(t("subscriptions.features.create.validation.unitPrice"));
      return;
    }

    const limit = parseOptionalFiniteNumber(limitInput);
    if (
      featureType === "NUMBER" &&
      limitInput.trim() !== "" &&
      limit === undefined
    ) {
      toast.error(t("subscriptions.features.create.validation.limit"));
      return;
    }

    try {
      await mutateAsync({
        title: normalizedTitle,
        key: normalizedKey,
        description: normalizedDescription || undefined,
        type: featureType,
        limit: featureType === "NUMBER" ? limit : undefined,
        unitPrice,
      });
      toast.success(t("subscriptions.features.create.successToast"));
      handleClose();
    } catch {
      toast.error(t("subscriptions.features.create.errorToast"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("subscriptions.features.create.title")}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <span className="font-medium text-sm leading-none text-zinc-900">
              {t("subscriptions.features.create.key")}
            </span>
            <Select
              value={selectedFeatureKey}
              onValueChange={(value) =>
                setSelectedFeatureKey(value as CanonicalFeatureKey)
              }
              disabled={isPending || availableFeatures.length === 0}
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={t("subscriptions.features.create.selectFeature")}
                />
              </SelectTrigger>
              <SelectContent>
                {availableFeatures.map((feature) => (
                  <SelectItem key={feature.key} value={feature.key}>
                    {feature.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {availableFeatures.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("subscriptions.features.create.noAvailableFeatures")}
            </p>
          ) : null}

          <Input
            label={t("subscriptions.features.create.featureTitle")}
            value={selectedFeature?.title ?? ""}
            readOnly
            disabled
          />

          <Input
            label={t("subscriptions.features.create.description")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <div className="flex flex-col gap-1.5">
            <span className="font-medium text-sm leading-none text-zinc-900">
              {t("subscriptions.features.create.type")}
            </span>
            <Input value={featureType} readOnly disabled />
          </div>

          {featureType === "NUMBER" ? (
            <Input
              type="number"
              inputMode="decimal"
              step="any"
              label={t("subscriptions.features.create.limit")}
              value={limitInput}
              onChange={(e) => setLimitInput(e.target.value)}
            />
          ) : null}

          <Input
            type="number"
            inputMode="decimal"
            step="any"
            label={t("subscriptions.features.create.unitPrice")}
            value={unitPriceInput}
            onChange={(e) => setUnitPriceInput(e.target.value)}
          />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isPending}
              onClick={handleClose}
            >
              {t("subscriptions.features.create.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={isPending || selectedFeature === null}
            >
              {t("subscriptions.features.create.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
